import React, { ReactNode, useMemo } from "react"
import CreatableSelect from "react-select/creatable"
import AsyncCreatableSelect from "react-select/async-creatable"
import { FormikProps } from "formik/dist/types"
import clsx from "clsx"
import { ObjectSchema } from "yup"
import useSelectReact from "./useSelectReact"
import useFormik from "./useFormik"
import axios from "axios"
import { debounce } from "lodash"

const MySelectCreatableFormField = ({
     name,
     label,
     formikProps,
     isClearable = true,
     placeholder,
     isValidNewOption,
     classNameLabel,
     classNameField,
     classNameLayout,
     autoFocus,
     validationSchema,
     endpoint,
     method,
     payload = {},
     minimumLengthSearch = 2,
     isMulti = true,
     onChangeCallback,
     options,
}: IPropsModel) => {
     const { formatGroupLabel, styles } = useSelectReact()
     const { isFieldValid, isFieldOnError, getFieldErrorMessage, isFieldRequired } = useFormik()

     const rawValue = formikProps?.getFieldProps(name).value

     const formatValue = (value: any) => {
          if (isMulti) {
               return Array.isArray(value) ? value.map(v => ({ label: v, value: v })) : []
          } else {
               return value ? { label: value, value: value } : null
          }
     }

     const formattedValue = formatValue(rawValue)

     const onChange = (val: any) => {
          const newValue = isMulti ? (val ? val.map((i: any) => i.value) : []) : val?.value ?? ""

          formikProps?.setFieldValue(name, newValue)
          if (onChangeCallback) {
               onChangeCallback(newValue)
          }
     }

     const fetchOptions = useMemo(() => {
          return debounce(async (inputValue: string, callback: (options: any[]) => void) => {
               if (!endpoint || inputValue.length < minimumLengthSearch) {
                    callback([])
                    return
               }

               try {
                    const query = { q: inputValue, ...payload }
                    const result = method === "GET" ? await axios.get(endpoint, { params: query }) : await axios.post(endpoint, query)

                    callback(result.data)
               } catch (err: any) {
                    callback([])
               }
          }, 250)
     }, [endpoint, method, payload, minimumLengthSearch])

     const sharedProps = {
          name,
          isClearable,
          isMulti,
          value: formattedValue,
          placeholder,
          onChange,
          formatCreateLabel: (inputValue: string) => <span className="fw-bold fs-6 bg-white text-primary py-1 px-2 rounded">Choisir "{inputValue}"</span>,
          isValidNewOption,
          styles: styles(!!(formikProps && isFieldOnError(formikProps, name)), !!(formikProps && isFieldValid(formikProps, name))),
          formatGroupLabel,
          noOptionsMessage: ({ inputValue }) => {
               // If static options are provided, show standard message, otherwise show search/create prompt
               if (options && options.length > 0) return "Aucun résultat"

               if (!endpoint) return placeholder
               if (inputValue.length < minimumLengthSearch) {
                    return `Saisissez au moins ${minimumLengthSearch} caractères pour lancer la recherche`
               }
               return "Aucun résultat"
          },
          onMenuClose: () => formikProps?.getFieldHelpers(name).setTouched(true),
          autoFocus,
          menuPortalTarget: document.body,
     }

     const loadOptions = (inputValue: string, callback: (options: any[]) => void) => {
          if (inputValue.length < minimumLengthSearch) {
               callback([])
               return
          }

          fetchOptions(inputValue, callback)
     }

     return (
          <div className={clsx("row", classNameLayout)}>
               <div className={clsx("col-12 d-flex align-items-center", classNameLabel)}>
                    {label && (
                         <label
                              className={clsx("fs-6 fw-bolder text-gray-900", {
                                   required: validationSchema && isFieldRequired(validationSchema, name),
                              })}
                         >
                              {label}
                         </label>
                    )}
               </div>
               <div className={clsx("col-12", classNameField, { "mt-1": label })}>
                    {endpoint ? (
                         <AsyncCreatableSelect {...sharedProps} loadOptions={loadOptions} cacheOptions isMulti={isMulti} />
                    ) : (
                         <CreatableSelect
                              {...sharedProps}
                              // Use passed options, OR fallback to existing logic (options = current value)
                              options={options || (formattedValue ? (Array.isArray(formattedValue) ? formattedValue : [formattedValue]) : [])}
                              isMulti={isMulti}
                         />
                    )}
                    {formikProps && isFieldOnError(formikProps, name) && <div className="text-danger">{getFieldErrorMessage(formikProps, name)}</div>}
               </div>
          </div>
     )
}

interface IPropsModel {
     label?: ReactNode
     name: string
     formikProps: FormikProps<any> | null
     isClearable?: boolean
     placeholder: string
     isValidNewOption?: (val: string) => boolean
     classNameLayout?: string
     classNameLabel?: string
     classNameField?: string
     autoFocus?: boolean
     validationSchema?: ObjectSchema<any>
     endpoint?: string
     method?: "GET" | "POST"
     payload?: Record<string, any>
     minimumLengthSearch?: number
     isMulti?: boolean
     onChangeCallback?: (val: string | string[]) => void
     options?: any[]
}

export default MySelectCreatableFormField
